import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  signOut,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  onAuthStateChanged
} from 'firebase/auth';
import '../firebase';  // 导入Firebase配置确保初始化

// Turnstile站点密钥
const TURNSTILE_SITE_KEY = '0x4AAAAAABeHRzkl42tw10a_';
// 密钥信息已移至服务器端

/**
 * 认证API服务
 * 封装Firebase认证功能并提供统一接口
 * 可以在将来轻松替换为自定义后端
 */
class AuthAPI {
  constructor() {
    this.auth = getAuth();
    this.googleProvider = new GoogleAuthProvider();
    this.githubProvider = new GithubAuthProvider();
  }

  /**
   * 获取Turnstile站点密钥
   * @returns {string} - Turnstile站点密钥
   */
  getTurnstileSiteKey() {
    return TURNSTILE_SITE_KEY;
  }

  /**
   * 验证Turnstile令牌
   * @param {string} token - Turnstile令牌
   * @returns {Promise<boolean>} - 验证结果
   */
  async verifyTurnstileToken(token) {
    try {
      // 在实际环境中，这个验证应该在后端进行
      // 这里我们模拟一个成功的验证
      // 由于这是前端代码，我们不应该在这里使用SECRET_KEY
      console.log('验证Turnstile令牌:', token);
      
      // 模拟网络请求延迟
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return {
        success: true,
        message: '验证成功'
      };
    } catch (error) {
      console.error('Turnstile验证错误:', error);
      return {
        success: false,
        message: '验证失败，请重试'
      };
    }
  }

  /**
   * 使用邮箱和密码登录
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @param {string} turnstileToken - Turnstile验证令牌
   * @returns {Promise<UserCredential>} - 登录结果
   */
  async loginWithEmail(email, password, turnstileToken) {
    try {
      // 首先验证Turnstile令牌
      if (turnstileToken) {
        const verificationResult = await this.verifyTurnstileToken(turnstileToken);
        if (!verificationResult.success) {
          return {
            success: false,
            message: '人机验证失败，请重试'
          };
        }
      }
      
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      return {
        success: true,
        user: result.user,
        message: '登录成功'
      };
    } catch (error) {
      console.error('Login Error:', error);
      return this._handleAuthError(error, '登录');
    }
  }

  /**
   * 使用邮箱和密码注册新用户
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @param {string} turnstileToken - Turnstile验证令牌
   * @returns {Promise<Object>} - 注册结果
   */
  async registerWithEmail(email, password, turnstileToken) {
    try {
      // 首先验证Turnstile令牌
      if (turnstileToken) {
        const verificationResult = await this.verifyTurnstileToken(turnstileToken);
        if (!verificationResult.success) {
          return {
            success: false,
            message: '人机验证失败，请重试'
          };
        }
      }
      
      const result = await createUserWithEmailAndPassword(this.auth, email, password);
      return {
        success: true,
        user: result.user,
        message: '注册成功'
      };
    } catch (error) {
      console.error('Register Error:', error);
      return this._handleAuthError(error, '注册');
    }
  }

  /**
   * 使用Google账号登录
   * @returns {Promise<Object>} - 登录结果
   */
  async loginWithGoogle() {
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      return {
        success: true,
        user: result.user,
        message: 'Google登录成功'
      };
    } catch (error) {
      console.error('Google Login Error:', error);
      return this._handleAuthError(error, 'Google登录');
    }
  }

  /**
   * 使用GitHub账号登录
   * @returns {Promise<Object>} - 登录结果
   */
  async loginWithGithub() {
    try {
      const result = await signInWithPopup(this.auth, this.githubProvider);
      return {
        success: true,
        user: result.user,
        message: 'GitHub登录成功'
      };
    } catch (error) {
      console.error('GitHub Login Error:', error);
      return this._handleAuthError(error, 'GitHub登录');
    }
  }

  /**
   * 创建手机验证码验证器
   * @param {string} containerId - 重新验证容器ID
   * @returns {RecaptchaVerifier} - 验证码验证器
   */
  createRecaptchaVerifier(containerId) {
    return new RecaptchaVerifier(this.auth, containerId, {
      'size': 'invisible',
      'callback': () => {
        console.log('reCAPTCHA已解决');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA已过期');
      },
      'error-callback': (error) => {
        console.error('reCAPTCHA错误:', error);
      },
      'hl': 'zh-CN',
      'isolated': true
    });
  }

  /**
   * 发送手机验证码
   * @param {string} phoneNumber - 手机号码
   * @param {RecaptchaVerifier} recaptchaVerifier - 验证码验证器
   * @returns {Promise<Object>} - 发送结果
   */
  async sendPhoneVerificationCode(phoneNumber, recaptchaVerifier) {
    try {
      // 格式化手机号码
      const formattedPhone = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+86${phoneNumber}`;
      
      const confirmationResult = await signInWithPhoneNumber(
        this.auth, 
        formattedPhone, 
        recaptchaVerifier
      );
      
      return {
        success: true,
        confirmationResult,
        message: '验证码已发送'
      };
    } catch (error) {
      console.error('Phone Verification Error:', error);
      return this._handleAuthError(error, '发送验证码');
    }
  }

  /**
   * 使用验证码登录
   * @param {ConfirmationResult} confirmationResult - 确认结果
   * @param {string} verificationCode - 验证码
   * @returns {Promise<Object>} - 登录结果
   */
  async verifyPhoneCode(confirmationResult, verificationCode) {
    try {
      const result = await confirmationResult.confirm(verificationCode);
      return {
        success: true,
        user: result.user,
        message: '手机号验证成功'
      };
    } catch (error) {
      console.error('Phone Login Error:', error);
      return this._handleAuthError(error, '验证码验证');
    }
  }

  /**
   * 退出登录
   * @returns {Promise<Object>} - 退出结果
   */
  async logout() {
    try {
      await signOut(this.auth);
      return {
        success: true,
        message: '退出登录成功'
      };
    } catch (error) {
      console.error('Logout Error:', error);
      return {
        success: false,
        message: '退出登录失败'
      };
    }
  }

  /**
   * 获取当前登录用户
   * @returns {User|null} - 当前用户
   */
  getCurrentUser() {
    return this.auth.currentUser;
  }

  /**
   * 监听认证状态变化
   * @param {Function} callback - 状态变化回调
   * @returns {Unsubscribe} - 取消监听函数
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(this.auth, callback);
  }

  /**
   * 处理认证错误
   * @param {Error} error - 错误对象
   * @param {string} operation - 操作名称
   * @returns {Object} - 格式化的错误信息
   */
  _handleAuthError(error, operation) {
    let message = `${operation}失败，请稍后再试`;
    
    switch (error.code) {
      case 'auth/invalid-email':
        message = '邮箱格式不正确';
        break;
      case 'auth/user-not-found':
        message = '用户不存在';
        break;
      case 'auth/wrong-password':
        message = '密码错误';
        break;
      case 'auth/email-already-in-use':
        message = '该邮箱已被注册';
        break;
      case 'auth/weak-password':
        message = '密码强度太弱';
        break;
      case 'auth/popup-closed-by-user':
        message = '登录窗口被关闭';
        break;
      case 'auth/cancelled-popup-request':
        message = '登录请求已取消';
        break;
      case 'auth/account-exists-with-different-credential':
        message = '该邮箱已使用其他登录方式注册';
        break;
      case 'auth/invalid-verification-code':
        message = '验证码错误';
        break;
      case 'auth/code-expired':
        message = '验证码已过期';
        break;
    }
    
    return {
      success: false,
      error: error,
      code: error.code,
      message: message
    };
  }
}

// 创建单例实例
const authAPI = new AuthAPI();
export default authAPI; 